import { z } from 'zod';

/**
 * Who can do what. Roles map 1:1 to Cognito user-pool groups.
 * - admin:  the owner — everything, including user management and publishing plans
 * - office: planning, sales, pricing, costs
 * - crew:   barn logging (mortality, feed) — phone-first
 * - viewer: read-only (e.g. an accountant or lender)
 */
export const ROLES = ['admin', 'office', 'crew', 'viewer'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Owner / admin',
  office: 'Office',
  crew: 'Barn crew',
  viewer: 'Viewer (read-only)',
};

/** When a user is in several groups, the most powerful one wins. */
const PRECEDENCE: readonly Role[] = ['admin', 'office', 'crew', 'viewer'];

export function effectiveRole(groups: readonly string[] | undefined | null): Role | null {
  if (!groups || groups.length === 0) return null;
  for (const role of PRECEDENCE) {
    if (groups.includes(role)) return role;
  }
  return null;
}

/** Local-development personas (only accepted when STAGE=local). */
export const DEV_PERSONAS = [
  { id: 'owner', name: 'Owner', email: 'owner@bnb.local', role: 'admin' },
  { id: 'office', name: 'Office', email: 'office@bnb.local', role: 'office' },
  { id: 'crew', name: 'Barn crew', email: 'crew@bnb.local', role: 'crew' },
  { id: 'viewer', name: 'Viewer', email: 'viewer@bnb.local', role: 'viewer' },
] as const satisfies ReadonlyArray<{ id: string; name: string; email: string; role: Role }>;
export type DevPersonaId = (typeof DEV_PERSONAS)[number]['id'];

/** The signed-in user as every layer sees it. `sub` is the Cognito user id (or the dev persona id). */
export const sessionUserSchema = z.object({
  sub: z.string().min(1),
  email: z.string(),
  name: z.string().optional(),
  role: roleSchema,
});
export type SessionUser = z.infer<typeof sessionUserSchema>;
